/**
 * Hub Overview — the four business metrics, the CP code to share and the
 * latest services. Metrics come from `counts` on the Services endpoint, which
 * always describes the CP's overall attributed activity (never filtered).
 */
import { Link } from '@tanstack/react-router';
import { ArrowRight, ClipboardList, Hourglass, PiggyBank, UserPlus } from 'lucide-react';
import { formatCurrency, formatDate } from '@finmin/utils';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ErrorMessage,
  Skeleton,
} from '@finmin/ui';
import { useChannelPartnerContext, useChannelPartnerServices } from '@/api/channel-partners';
import { formatExpectedClosure, toNumber } from '../format';
import { ChannelPartnerCodeCard } from './code-card';
import { HubEmptyState, HubStatTile, ServiceStatusBadge } from './hub-bits';

const RECENT_COUNT = 5;

export function ChannelPartnerHubOverview() {
  const me = useChannelPartnerContext();
  const services = useChannelPartnerServices({ page: 1, page_size: RECENT_COUNT });
  const counts = services.data?.counts;
  const loading = services.isLoading;

  return (
    <div className="space-y-6">
      <ErrorMessage message={services.error?.message ?? null} />

      <section aria-label="Your activity" className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <HubStatTile
          label="Customers Onboarded"
          icon={UserPlus}
          isLoading={loading}
          value={counts?.customers_onboarded ?? 0}
          hint={
            counts
              ? `${counts.converted_customers} with a completed service`
              : undefined
          }
        />
        <HubStatTile
          label="Total Service Requests"
          icon={ClipboardList}
          isLoading={loading}
          value={counts?.total_service_requests ?? 0}
        />
        <HubStatTile
          label="Pending Services"
          icon={Hourglass}
          isLoading={loading}
          value={counts?.pending_services ?? 0}
        />
        <HubStatTile
          label="Money Saved by Customers"
          icon={PiggyBank}
          isLoading={loading}
          value={formatCurrency(toNumber(counts?.money_saved) ?? 0)}
          hint="Across completed services"
        />
      </section>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_380px] lg:items-start">
        <Card>
          <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0 pb-3">
            <div>
              <CardTitle className="text-base">Latest services</CardTitle>
              <CardDescription>Newest requests from customers you onboarded.</CardDescription>
            </div>
            <Link
              to="/channel-partner/hub/services"
              className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-accent underline-offset-4 hover:underline"
            >
              View all <ArrowRight className="h-3.5 w-3.5" aria-hidden />
            </Link>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : !services.data || services.data.items.length === 0 ? (
              <HubEmptyState
                icon={ClipboardList}
                title="No services yet"
                description="When a customer who signed up with your code starts a service, it shows up here."
              />
            ) : (
              <ul className="divide-y divide-border">
                {services.data.items.map((s) => (
                  <li key={s.id} className="flex items-center justify-between gap-3 py-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-foreground">{s.service_name}</p>
                      <p className="truncate text-xs text-muted-foreground">
                        {s.customer_name || 'Customer'} · Started {formatDate(s.created_at)}
                        {formatExpectedClosure(s) ? ` · Expected closure ${formatExpectedClosure(s)}` : ''}
                      </p>
                    </div>
                    <ServiceStatusBadge service={s} />
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <ChannelPartnerCodeCard code={me.data?.profile?.cp_code ?? null} />
      </div>
    </div>
  );
}
